import { useQueryClient } from '@tanstack/react-query'
import { Session } from '@supabase/supabase-js'
import { SupabaseAuthClient } from '@supabase/supabase-js/dist/module/lib/SupabaseAuthClient'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ORG_USER_QUERY_STALE_MS,
  fetchOrgUserRow,
  orgUserQueryKey,
} from '../../api/db/orgUserQuery'
import { queryKeys } from '../../api/db/queryKeys'
import { trackEvent } from '../../api/db/apiAnalytics'
import { useSupabaseClient } from '../../api/supabase'
import { paths } from '../../routes/paths'
import { getCurrencyForVisitor } from 'src/shared/utils/currency'
import {
  TEMP_AGENT_EMAIL,
  TEMP_AGENT_FORCE_LOGGED_IN,
} from './tempAgentAuthBypass'

const AuthContext = createContext<{
  auth: SupabaseAuthClient | null
  session: Session | null | undefined
  loading: boolean
}>({ auth: null, session: null, loading: true })

export const AuthProvider = ({ children }: any) => {
  const [session, setSession] = useState<Session | null>()
  const [loading, setLoading] = useState(true)
  const navigation = useNavigate()
  const supabaseClient = useSupabaseClient()
  const queryClient = useQueryClient()
  const currencyUpdateAttempted = useRef(false)
  /** Prevents repeated sign-out/sign-in-anon for the same auth user id when org is missing. */
  const noOrgRecoveryAttemptedForUserId = useRef<string | null>(null)

  const ensureAnonymousUser = async (session: Session | null) => {
    try {
      if (session?.user) {
        return session.user
      }

      const { data, error } = await supabaseClient.auth.signInAnonymously()
      if (error) {
        console.error('Error creating anonymous user:', error)
        throw error
      }

      return data.user
    } catch (error) {
      console.error('Failed to ensure anonymous user:', error)
      throw error
    }
  }

  useEffect(() => {
    const setData = async () => {
      const {
        data: { session },
      } = await supabaseClient.auth.getSession()
      setSession(session)
      try {
        await ensureAnonymousUser(session)
      } catch {
        // Logged in ensureAnonymousUser; render signed-out rather than hang.
      } finally {
        setLoading(false)
      }
    }

    const { data: listener } = supabaseClient.auth.onAuthStateChange(
      (_event, session) => {
        if (_event === 'SIGNED_OUT') {
          navigation(paths.loginSignUp)
          currencyUpdateAttempted.current = false
        }

        if (
          _event === 'SIGNED_IN' &&
          session?.user &&
          !session.user.is_anonymous
        ) {
          trackEvent(supabaseClient, {
            name: 'login',
            onceKey: `login:${session.user.id}`,
          })
        }

        setSession(session)
        // Anon role has no table grants: hold rendering until signInAnonymously lands.
        if (session || _event === 'SIGNED_OUT') setLoading(false)
      }
    )

    setData()

    return () => {
      listener?.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (TEMP_AGENT_FORCE_LOGGED_IN) return
    if (!session?.user?.id) return
    const userId = session.user.id

    if (noOrgRecoveryAttemptedForUserId.current === userId) return

    let cancelled = false

    const recoverAnonymousSessionIfNoOrg = async () => {
      if (new URLSearchParams(window.location.search).has('oauth')) {
        return
      }
      if (window.location.pathname === paths.invitation) {
        return
      }

      const orgUserResult = await queryClient.fetchQuery({
        queryKey: orgUserQueryKey(userId),
        queryFn: () => fetchOrgUserRow(supabaseClient, userId),
        staleTime: ORG_USER_QUERY_STALE_MS,
      })

      if (cancelled) return
      if (orgUserResult.data) return
      if (cancelled) return
      if (noOrgRecoveryAttemptedForUserId.current === userId) return

      noOrgRecoveryAttemptedForUserId.current = userId

      try {
        currencyUpdateAttempted.current = false
        await supabaseClient.auth.signOut()
        // Do not bail on `cancelled` after signOut — session becomes null and the effect
        // cleanup would otherwise skip signInAnonymously and leave the user signed out.
        const { error } = await supabaseClient.auth.signInAnonymously()
        if (error) {
          console.error('Failed to sign in anonymously after no-org recovery:', error)
          return
        }
        await queryClient.invalidateQueries({ queryKey: [queryKeys.org_user] })
        await queryClient.invalidateQueries({ queryKey: [queryKeys.organization] })
        await queryClient.invalidateQueries({ queryKey: [queryKeys.user] })
      } catch (error) {
        console.error('No-org recovery failed:', error)
      }
    }

    recoverAnonymousSessionIfNoOrg()

    return () => {
      cancelled = true
    }
  }, [session?.user?.id, supabaseClient, queryClient])

  useEffect(() => {
    if (!session?.user?.id || currencyUpdateAttempted.current) return

    const updateOrgCurrencyFromVisitor = async () => {
      try {
        const orgUserResult = await queryClient.ensureQueryData({
          queryKey: orgUserQueryKey(session.user.id),
          queryFn: () => fetchOrgUserRow(supabaseClient, session.user.id),
          staleTime: ORG_USER_QUERY_STALE_MS,
        })

        const orgId = orgUserResult.data?.organization_id
        if (!orgId) return

        const { data: org } = await supabaseClient
          .from('organization')
          .select('currency')
          .eq('id', orgId)
          .single()
        if (org?.currency != null) return

        currencyUpdateAttempted.current = true
        const currency = await getCurrencyForVisitor()
        await supabaseClient
          .from('organization')
          .update({ currency })
          .eq('id', orgId)
        queryClient.invalidateQueries({ queryKey: [queryKeys.organization] })
      } catch (error) {
        console.error('Failed to update organization currency:', error)
      }
    }

    updateOrgCurrencyFromVisitor()
  }, [session?.user?.id, supabaseClient, queryClient])

  const value = { auth: supabaseClient.auth, session, loading }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const value = useContext(AuthContext)

  if (!TEMP_AGENT_FORCE_LOGGED_IN) return value

  const session = value.session
  if (!session?.user) {
    return { ...value, loading: false }
  }

  return {
    ...value,
    loading: false,
    session: {
      ...session,
      user: {
        ...session.user,
        email: session.user.email || TEMP_AGENT_EMAIL,
        is_anonymous: false,
      },
    },
  }
}
